---
title: "Калькуляторы дат и времени без таблиц: timestamp и разница между датами"
description: "Когда нужен timestamp, epoch days, миллисекунды или разница между датами, dateify помогает считать локально и без ручных формул."
locale: "ru"
date: "2026-06-29"
tags: ["timestamp", "даты", "калькулятор дат"]
relatedTools: ["timestamp", "date-difference"]
translations:
  en: "date-and-time-calculators-without-spreadsheets"
faq:
  - q: "Timestamp хранит часовой пояс?"
    a: "Нет. Timestamp описывает момент времени, а dateify помогает сравнить локальное отображение, UTC/ISO-представление и разные масштабы числа."
  - q: "Можно ли использовать рабочие дни для юридических расчётов?"
    a: "Калькулятор рабочих дней исключает субботу и воскресенье, но не учитывает праздники. Для юридических задач это предварительная оценка."
---

В логе стоит `1719820800000`, в API-документации написано `expires_at`, а
менеджер спрашивает, сколько рабочих дней осталось до запуска. Это не одна
задача про даты, а три разных проверки: формат timestamp, читаемое время и
период между датами.

[dateify.ru](/) закрывает их как быстрые локальные калькуляторы. Детальные
разборы остаются в отдельных статьях, а эта страница работает как карта:
какой инструмент открыть и чего от него не ждать.

| Сценарий | Открыть | Что проверить |
| --- | --- | --- |
| Лог или API отдаёт большое число | [timestamp](/timestamp) | секунды, миллисекунды, наносекунды, epoch days |
| Нужно понять срок между двумя датами | [разницу между датами](/raznica-mezhdu-datami) | включать ли конечный день, нужны ли рабочие дни |
| Юридический или кадровый срок | [разницу между датами](/raznica-mezhdu-datami) | использовать как черновую оценку и сверять праздники отдельно |

## Timestamp: секунды, миллисекунды и не только

[Конвертер timestamp](/timestamp) нужен, когда одно число надо быстро
проверить в человеческом виде. Самая частая ошибка - перепутать секунды и
миллисекунды. `1719820800` и `1719820800000` выглядят похоже, но это разные
масштабы. Хороший конвертер должен явно показывать оба варианта, а не молча
угадывать.

Для разработчиков важны и дополнительные представления: ISO-строка, epoch days,
наносекунды, локальное время. Это помогает сверить логи, TTL, cron-настройки,
срок действия токена или дату из API.

Если главная проблема - понять, в чём пришло число, откройте подробный разбор
[Unix timestamp: секунды или миллисекунды](/blog/unix-timestamp-sekundy-ili-millisekundy).

## Разница между датами

[Разница между датами](/raznica-mezhdu-datami) полезна для дедлайнов, отпусков,
периодов договора, учебных сроков и планирования. Важная настройка - включать
ли конечную дату. Для "с 1 по 10 число включительно" результат отличается от
простой разницы между двумя отметками.

Рабочие дни считаются как понедельник-пятница. Праздники не вычитаются, поэтому
для кадровых, налоговых или юридических расчётов результат надо проверять по
официальному календарю. Зато для продуктовых дедлайнов и быстрой оценки этого
часто достаточно.

Если нужно именно посчитать период, а не выбирать между инструментами, есть
отдельная инструкция [как посчитать разницу между датами](/blog/kak-poschitat-raznicu-mezhdu-datami).

## Почему локально

После расчёта сохраните не только итоговое число, но и правило подсчёта:
включали ли конечный день, какой масштаб timestamp проверяли и считали ли
выходные. Именно эти детали чаще всего расходятся между задачей, таблицей и
кодом.
